const defaultSrc = () => {
  return ['default-src', "'self'"].join(' ');
};

const connectSrc = ({ apiUrl }) => {
  const directive = [
    'connect-src',
    "'self'",
    '*.zapier.com',
    '*.zapier-staging.com',
    // For Wistia video player, which connects to, e.g., 'embedwistia-a.akamaihd.net'.
    '*.akamaihd.net',
    // For Wistia video player, which connects to, e.g., 'fg8vvsvnieiv3ej16jby.litix.io'.
    '*.litix.io',
    // For Wistia video player, which connects to, e.g., 'distillery.wistia.com', 'pipedream.wistia.com'.
    '*.wistia.com',
    // For Swiftype, which connects to, e.g., 'host-1234ab.api.swiftype.com'.
    '*.api.swiftype.com',
    // For Sentry, which connects to, e.g. '123456@abcd.ingest.sentry.io'
    '*.ingest.sentry.io',
    'app.getsentry.com',
    'rs.fullstory.com',
    // For Clearbit
    // Owned by @Doug.Lee and the marketing team, injected by Google Tag Manager
    'x.clearbit.com',
    // For Google One Tap V2
    'accounts.google.com',
    'stats.g.doubleclick.net',
    'www.google-analytics.com',
    // For temporary tree test embed. See <OptimalWorkshopScript />.
    'www.optimalworkshop.com',
    // For chat embed for Project Jupiter (companies, pricing).
    '*.olark.com',
    // For Datadog RUM
    '*.datadoghq.com',
    // labs voice/chat experiment
    'us-central1-zapier-faq-poc-vinsed.cloudfunctions.net',
    // For Braintree/PayPal payment processor
    'api.sandbox.braintreegateway.com',
    'api.braintreegateway.com',
    'client-analytics.sandbox.braintreegateway.com',
    'client-analytics.braintreegateway.com',
    '*.braintree-api.com',
    // For Stripe payment processor
    'https://api.stripe.com',
    // "approval by zapier" app
    'tables.zapier.com',
  ];

  if (typeof apiUrl === 'string') {
    directive.push(apiUrl);
  }

  return directive.join(' ');
};

const fontSrc = () => {
  return [
    'font-src',
    "'self'",
    'fonts.googleapis.com',
    // For various Google products, including Google Optimize.
    'fonts.gstatic.com',
    // Some parts of Zapier continue to use maxcdn.bootstrapcdn.com. CSP
    // violation reports were indicating it was in use for fonts on app profile
    // pages. We couldn't locate the usage on app profile pages, but the CDN is
    // being added to the CSP anyway, just to be safe.
    'maxcdn.bootstrapcdn.com',
    // Used by react-lazyload
    'data:',
  ].join(' ');
};

const frameSrc = ({ apiUrl }) => {
  const directive = [
    'frame-src',
    "'self'",
    'zapier.com',
    'zapier-staging.com',
    'fast.wistia.com',
    'fast.wistia.net',
    // For Google Optimize.
    'optimize.google.com',
    'smartlock.google.com',
    // For Google One Tap V2
    'accounts.google.com',
    // For Google Captcha
    'www.google.com/',
    'dis.us.criteo.com',
    'www.youtube.com',
    'player.vimeo.com',
    // For chat embed for Project Jupiter (companies, pricing).
    'static.olark.com',
    // Zapier Labs voice chat
    'https://console.dialogflow.com',
    // https://devdocs.drift.com/docs/enabling-drift-in-your-apps-content-security-policy-csp
    'https://js.driftt.com',
    // Qualaroo Surveys
    'dntcl.qualaroo.com',
    // For Braintree/PayPal payment processor
    'assets.braintreegateway.com',
    '*.paypal.com',
    // For Stripe payment processor
    'https://js.stripe.com',
    'https://hooks.stripe.com',
    // "approval by zapier" app
    'tables.zapier.com',
  ];

  if (typeof apiUrl === 'string') {
    directive.push(apiUrl);
  }

  return directive.join(' ');
};

const imgSrc = () => {
  return [
    'img-src',
    "'self'",
    // For Wistia video player play button image, which is served by Wistia as
    // an inline data URI:
    // https://cdn.zapier.com/storage/photos/610ff60ee49b8058bfb8f736ee898743.png
    'data:',
    // Unfortunately Google Analytics loads its tracking pixel by first
    // requesting https://www.google.com/ads/ga-audiences, which then 302
    // redirects to https://www.google.TLD/ads/ga-audiences, where TLD is the
    // country code TLD (e.g. .ca for Canadians) for that user:
    //
    // https://security.stackexchange.com/questions/145385/csp-allowing-all-google-domains
    //
    // At the time of this writing, there is no clean way to work around this
    // problem. All image domains must be permitted in order for Google
    // Analytics to function. The only semi-viable alternative would be to
    // enumerate every Google domain used by Google Analytics within the CSP,
    // which could become a maintenance problem as TLDs are added and removed from the list.
    'https://*',
    // For Google Analytics and Google Optimize
    'www.google-analytics.com',
    // For Google Optimize
    'optimize.google.com',
    // For chat embed for Project Jupiter (companies, pricing).
    'log.olark.com',
    // For Braintree & PayPal payment gateways
    'assets.braintreegateway.com',
    'checkout.paypal.com',
  ].join(' ');
};

const mediaSrc = () => {
  return [
    'media-src',
    "'self'",
    // For Wistia video player, which will not play videos if it cannot use 'blob:' URIs.
    // https://cdn.zapier.com/storage/photos/0f3a92a603c8a2a7ce6c2b9da6af7124.png
    'blob:',
    // For Wistia video player, which refuses to play in Safari if it cannot use 'data:' URIs.
    'data:',
    // For Wistia video player, which refuses to play in Safari unless it can connect to, e.g., 'embedwistia-a.akamaihd.net'.
    '*.akamaihd.net',
    'cdn.zapier.com',
    // For Wistia video player, which refuses to play in Safari unless it can connect to, e.g., 'embed-fastly.wistia.com'.
    'embed-fastly.wistia.com',
    // For Olark chat embed for Project Jupiter (companies, pricing).
    'static.olark.com',
  ].join(' ');
};

const scriptSrc = ({ CDN_HOST, CSP_ALLOW_UNSAFE_SCRIPTS, nonce }) => {
  const directive = ['script-src'];

  directive.push("'self'");

  // This is required to work around a lack of CSP 2.0 support in Edge.
  // Specifically, Edge does not support nonces or hashes:
  //
  // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/13246371/
  //
  // This can be worked around by placing nonce- and hash- sources *after* the
  // unsafe-inline source. Browsers that support CSP 2.0 will ignore
  // unsafe-inline, and Edge will ignore the nonce and hash sources that it
  // does not understand.
  //
  // https://github.com/IdentityServer/IdentityServer4/issues/475#issuecomment-260225024
  //
  // "Specifying a nonce [or a hash] makes a modern browser ignore 'unsafe-inline'":
  //
  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Security-Policy/script-src
  //
  // Of course, even though unsafe-inline is specified, Edge will still log a
  // warning to the console suggesting that the resource will be blocked. But
  // it won't be blocked. unsafe-inline is indeed respected, and the inline
  // script tag will function correctly:
  //
  // https://cdn.zapier.com/storage/photos/27ce4e62091dc7b46ae1a803cc717032.png
  //
  // Even worse, Edge and Firefox will ignore unsafe-inline when a 304 response
  // is returned and dynamic nonces are used.
  //
  // https://github.com/w3c/webappsec-csp/issues/161
  //
  // The original 200 response contained nonce A in both the header and body.
  // But the 304 response reads that body from the browser's cache, while
  // sending nonce B in the header. Edge and Firefox get confused by the
  // mismatched nonces and block all inline scripts as a result. To work around
  // this, the CSP *must* be implemented as a <meta> tag, which embeds the
  // policy within the response body so that Edge/Firefox see a consistent
  // nonce in the event of 304 responses. Here is an example of the errors
  // emitted when the nonce is mismatched:
  //
  // Edge:
  // https://cdn.zapier.com/storage/photos/aada509586d8ccff83aa88b22390e91d.png
  // FF:
  // https://cdn.zapier.com/storage/photos/8355cc8d8a99b9716391a9d5ddb0886c.png
  directive.push("'unsafe-inline'");

  // unsafe-eval is required by GTM, which uses eval heavily.
  //
  // unsafe-eval is required to prevent webpack from breaking in the
  // local development environment.
  directive.push("'unsafe-eval'");

  if (typeof CDN_HOST === 'string') {
    directive.push(CDN_HOST);
  }

  // For Google One Tap V2
  directive.push('accounts.google.com');
  directive.push('fast.wistia.com');
  directive.push('fullstory.com');
  directive.push('smartlock.google.com');

  // For Google Analytics and Google Optimize
  directive.push('www.google-analytics.com');

  // For Google Optimize
  directive.push('optimize.google.com');

  // Even though www.googletagmanager.com is backed by a nonce, it must be
  // whitelisted here to support Edge, which does not support nonces.
  directive.push('tagmanager.google.com');
  directive.push('www.googletagmanager.com');

  directive.push('zapier.com');

  // Allow blob URLs generated for embedded Wistia videos
  // https://stackoverflow.com/questions/30864573/what-is-a-blob-url-and-why-it-is-used
  directive.push('blob:');

  // For chat embed for Project Jupiter (companies, pricing).
  directive.push('*.olark.com');
  directive.push('https://js.driftt.com');

  // For Braintree/PayPal payment processor
  directive.push('js.braintreegateway.com');
  directive.push('assets.braintreegateway.com');
  directive.push('www.paypalobjects.com');
  directive.push('*.paypal.com');

  // For Stripe payment processor
  directive.push('https://js.stripe.com');

  // The nonce must be the last item in the list of script policies, aside from
  // strict-dynamic, to work around a lack of CSP 2.0 nonce or hash support in
  // Edge. Edge will only respect CSP sources which occur before the
  // nonce/hash ones that it does not understand.
  directive.push(`'nonce-${nonce}'`);

  if (CSP_ALLOW_UNSAFE_SCRIPTS === 'true') {
    // strict-dynamic is required to allow the React error overlay to be
    // injected in the local development environment. strict-dynamic
    // should be the very last item within script-src for backwards compatibility
    // with browsers that do not support CSP 3.0:
    // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Security-Policy/script-src#strict-dynamic
    directive.push("'strict-dynamic'");
  }

  return directive.join(' ');
};

const styleSrc = ({ CDN_HOST }) => {
  const directive = ['style-src'];

  directive.push("'self'");

  // This is required to support emotion (CSS in JS) library's dynamically
  // injected style tags.
  //
  // Also required to support Google Optimize.
  directive.push("'unsafe-inline'");

  if (typeof CDN_HOST === 'string') {
    directive.push(CDN_HOST);
  }

  // For various Google services including Google Optimize
  directive.push('fonts.googleapis.com');

  // For Google One Tap V2
  directive.push('accounts.google.com');

  // For Google Optimize
  directive.push('optimize.google.com');

  // For Google Tag Manager (GTM)
  directive.push('tagmanager.google.com');

  // For chat embed for Project Jupiter (companies, pricing).
  directive.push('static.olark.com');

  return directive.join(' ');
};

const contentSecurityPolicy = options => {
  // According to the spec, the report-uri directive must be configued in the
  // Content-Security-Policy header, while the rest of the policy is delivered
  // in a <meta> tag for Firefox/Edge compatibility.
  return [
    defaultSrc(),
    connectSrc(options),
    fontSrc(),
    frameSrc(options),
    imgSrc(),
    mediaSrc(),
    scriptSrc(options),
    styleSrc(options),
  ].join('; ');
};

module.exports = contentSecurityPolicy;
